import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft, Check, X, Timer, Trophy, Share2, RotateCcw, Shuffle, Sparkles,
  Flame, Zap, Crown, Lock, ChevronRight, Star, Award, Users, Copy,
} from "lucide-react";
import { getAllQuizzes, type DBQuiz } from "@/lib/content.functions";
import { getGameState, saveGameState } from "@/lib/game.functions";
import { createChallenge } from "@/lib/challenges.functions";
import { logQuizSession } from "@/lib/quiz-stats.functions";
import { trackConversion } from "@/hooks/use-page-view";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import { useUserXp } from "@/hooks/use-user-xp";


export const Route = createFileRoute("/play")({
  validateSearch: (search: Record<string, unknown>): { quiz?: string } => ({
    quiz: typeof search.quiz === "string" ? search.quiz.slice(0, 120) : undefined,
  }),
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: ["play-quizzes"],
      queryFn: () => getAllQuizzes(),
    }),
  head: () => {
    const url = "https://smartasaurus.com/play";
    const title = "Smartasaurus - The Science Curiosity Game";
    const desc = "Tap. Reveal. Get smarter. The science trivia game built for short attention spans and big 'holy shit' moments.";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: PlayPage,
  errorComponent: ({ reset }) => {
    const router = useRouter();
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6 text-center">
        <div>
          <p className="text-muted-foreground">Couldn't start the game.</p>
          <button onClick={() => { router.invalidate(); reset(); }} className="mt-4 rounded-full bg-primary px-4 py-2 text-sm font-bold uppercase text-primary-foreground">Retry</button>
        </div>
      </div>
    );
  },
  notFoundComponent: () => <div className="p-6">Not found.</div>,
});

// ---- Category classification ----
type CatKey = "animals" | "space" | "body" | "weird" | "random";
const ANIMAL_RE = /(animal|frog|octop|shark|whale|dolphin|jellyf|tardigrade|platypus|sloth|koala|wombat|woodpecker|vulture|ostrich|cuckoo|peregrine|crocodile|caterpillar|butterfly|bee|ant|spider|woodlouse|lobster|shrimp|horseshoe|pufferfish|polar bear|bear|cat\b|dog\b|bird|fish|mammal|reptile|insect|colossal|giant squid|box jellyfish|gympie|brazilian|fire ant|dust mite|demodex|mite|cone snail|snail|venom|species|predator|prey)/i;
const SPACE_RE = /(space|planet|moon|sun\b|star|galaxy|cosmic|gravity|gravitational|black hole|neutron|saturn|jupiter|mars|venus|mercury|uranus|neptune|pluto|asteroid|comet|nebula|orbit|light[- ]year|astronaut|nasa|telescope|universe|cosmolog|spaghett|stellar|io\b|titan|europa)/i;
const BODY_RE = /(human body|body|brain|heart|blood|bone|skin|eye|cornea|ear\b|nose|tongue|stomach|intestine|liver|kidney|lung|muscle|neuron|nerve|dna\b|cell|bacteria|microbi|immune|hormone|saliva|sweat|sleep|dream|memory|tooth|teeth|hair|nail|baby|pregnan|hiccup|sneeze|breath|digest)/i;

function classify(q: DBQuiz): Exclude<CatKey, "random"> {
  // Prefer the authoritative DB category when present; fall back to regex.
  const dbCat = (q.category ?? "").toLowerCase();
  if (dbCat === "animals" || dbCat === "space" || dbCat === "body" || dbCat === "weird") return dbCat;
  const text = `${q.slug} ${q.question} ${q.explanation ?? ""}`;
  if (SPACE_RE.test(text)) return "space";
  if (ANIMAL_RE.test(text)) return "animals";
  if (BODY_RE.test(text)) return "body";
  return "weird";
}

// ---- Game constants ----
const QUESTIONS_PER_GAME = 10;
type Difficulty = "beginner" | "intermediate" | "expert";
const TIMER_BY_DIFF: Record<Difficulty, number> = {
  beginner: 25,     // relaxed - read comfortably, no pressure
  intermediate: 18, // moderate
  expert: 12,       // tight
};


type CatMeta = { key: CatKey; label: string; emoji: string; gradient: string; tagline: string; masterTitle: string };
const CATEGORIES: CatMeta[] = [
  { key: "animals", label: "Animals", emoji: "🦎", gradient: "from-emerald-600 via-teal-700 to-slate-900", tagline: "Nature is unhinged", masterTitle: "Animal Expert" },
  { key: "space",   label: "Space",   emoji: "🪐", gradient: "from-indigo-700 via-purple-800 to-slate-900", tagline: "The universe is weirder than you think", masterTitle: "Space Nerd" },
  { key: "body",    label: "Human Body", emoji: "🧠", gradient: "from-rose-700 via-red-800 to-slate-900", tagline: "You're a walking horror show", masterTitle: "Human Encyclopedia" },
  { key: "weird",   label: "Weird Science", emoji: "⚗️", gradient: "from-amber-600 via-orange-700 to-slate-900", tagline: "Reality is broken", masterTitle: "Weird Science Addict" },
  { key: "random",  label: "Mixed",   emoji: "🎲", gradient: "from-fuchsia-700 via-violet-800 to-slate-900", tagline: "All categories. Pure chaos.", masterTitle: "Smartasaurus" },
];

const RANKS = [
  { min: 0,  max: 25,  label: "Curious Rookie",       line: "You just walked into the Smartasaurus club. Your brain is warming up.", emoji: "🌱" },
  { min: 26, max: 50,  label: "Curiosity Explorer",   line: "You're already more curious than 90% of humans. Keep going.", emoji: "🧭" },
  { min: 51, max: 75,  label: "Genius in Disguise",   line: "Nobody sees it yet - but you're getting dangerously smart.", emoji: "🧪" },
  { min: 76, max: 90,  label: "Walking Encyclopedia", line: "This isn't a fluke. You belong here. Real Smartasaurus energy.", emoji: "📚" },
  { min: 91, max: 100, label: "Certified Smartasaurus", line: "Legendary. Our T-Rex bows. Screenshot this - you earned it.", emoji: "🦖" },
];
function rankFor(p: number) { return RANKS.find(r => p >= r.min && p <= r.max) ?? RANKS[0]; }

function shuffle<T>(arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
  return a;
}

type GameQ = { q: DBQuiz; opts: string[]; correctIdx: number };
function prepQuestion(q: DBQuiz): GameQ {
  const idxs = shuffle(q.options.map((_, i) => i));
  return { q, opts: idxs.map(i => q.options[i]), correctIdx: idxs.indexOf(q.answer_index) };
}

/**
 * Heuristic difficulty score for a quiz question (higher = harder).
 * Cheap proxy: longer questions, longer / more similar options, more options,
 * and presence of numbers/units tend to be harder.
 */
function difficultyScore(q: DBQuiz): number {
  const qLen = (q.question ?? "").length;
  const opts = q.options ?? [];
  const avgOpt = opts.length ? opts.reduce((a, o) => a + (o?.length ?? 0), 0) / opts.length : 0;
  const numeric = /\d/.test(q.question ?? "") ? 12 : 0;
  return qLen * 0.6 + avgOpt * 1.2 + opts.length * 4 + numeric;
}

/**
 * Order picks for gentle learning curve: easiest first, hardest last,
 * with light randomization inside each tier so rounds still feel fresh.
 */
function orderByDifficulty(picks: GameQ[]): GameQ[] {
  const sorted = picks.slice().sort((a, b) => difficultyScore(a.q) - difficultyScore(b.q));
  const n = sorted.length;
  const easyEnd = Math.ceil(n * 0.4);   // first 40% easy
  const medEnd  = Math.ceil(n * 0.75);  // next 35% medium, last 25% hard
  const easy = shuffle(sorted.slice(0, easyEnd));
  const med  = shuffle(sorted.slice(easyEnd, medEnd));
  const hard = shuffle(sorted.slice(medEnd));
  return [...easy, ...med, ...hard];
}

// ---- Persistent stats (localStorage) ----
type RoundHistory = {
  cat: CatKey; difficulty: Difficulty; score: number; correct: number; total: number;
  bestStreak: number; playedAt: string;
};
type Stats = {
  totalPlays: number;
  totalCorrect: number;
  bestScore: number;
  bestStreak: number;
  perCat: Record<Exclude<CatKey, "random">, { correct: number; bestScore: number; plays: number }>;
  achievements: string[];
  factsUnlocked: number;
  history?: RoundHistory[];
  recentSlugs?: string[];
};
const STATS_KEY = "smartasaurus.game.v2";
const IN_PROGRESS_KEY = "smartasaurus.game.round.v1";
const RECENT_SLUG_LIMIT = 40;
const DEFAULT_STATS: Stats = {
  totalPlays: 0, totalCorrect: 0, bestScore: 0, bestStreak: 0,
  perCat: { animals: {correct:0,bestScore:0,plays:0}, space: {correct:0,bestScore:0,plays:0}, body: {correct:0,bestScore:0,plays:0}, weird: {correct:0,bestScore:0,plays:0} },
  achievements: [], factsUnlocked: 0, history: [], recentSlugs: [],
};
function loadStats(): Stats {
  try { const raw = localStorage.getItem(STATS_KEY); if (!raw) return DEFAULT_STATS; const p = JSON.parse(raw); return { ...DEFAULT_STATS, ...p, perCat: { ...DEFAULT_STATS.perCat, ...(p.perCat ?? {}) }, history: p.history ?? [], recentSlugs: p.recentSlugs ?? [] }; } catch { return DEFAULT_STATS; }
}
function saveStats(s: Stats) { try { localStorage.setItem(STATS_KEY, JSON.stringify(s)); } catch {} }

// ---- In-progress round (allows resume after refresh / accidental close) ----
type HostAnswer = { o: number | null; s: number };
type InProgress = {
  answers?: HostAnswer[];
  cat: CatMeta;
  diff: Difficulty;
  round: GameQ[];
  idx: number;
  score: number;
  streak: number;
  bestStreakRound: number;
  speedWins: number;
  correctCount: number;
  savedAt: number;
};
function loadInProgress(): InProgress | null {
  if (typeof window === "undefined") return null;
  try { const raw = localStorage.getItem(IN_PROGRESS_KEY); if (!raw) return null; return JSON.parse(raw) as InProgress; } catch { return null; }
}
function saveInProgress(p: InProgress | null) {
  if (typeof window === "undefined") return;
  try { if (p) localStorage.setItem(IN_PROGRESS_KEY, JSON.stringify(p)); else localStorage.removeItem(IN_PROGRESS_KEY); } catch {}
}

// Stable per-device player key (for anon multiplayer results)
function playerKey(): string {
  if (typeof window === "undefined") return "server";
  try {
    let k = localStorage.getItem("smartasaurus.player.key");
    if (!k) { k = `p_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`; localStorage.setItem("smartasaurus.player.key", k); }
    return k;
  } catch { return `p_${Math.random().toString(36).slice(2, 10)}`; }
}

type Achievement = { id: string; label: string; desc: string; emoji: string; check: (s: Stats, ctx: RoundCtx) => boolean };
type RoundCtx = { cat: CatKey; correct: number; score: number; bestStreak: number; speedWins: number; perfect: boolean };
const ACHIEVEMENTS: Achievement[] = [
  { id: "first_blood", label: "First Blood",   desc: "Get your first answer right",     emoji: "🩸", check: s => s.totalCorrect >= 1 },
  { id: "streak_3",    label: "On Fire",        desc: "3-question streak",                emoji: "🔥", check: (_, c) => c.bestStreak >= 3 },
  { id: "streak_5",    label: "Unstoppable",    desc: "5-question streak",                emoji: "⚡", check: (_, c) => c.bestStreak >= 5 },
  { id: "streak_10",   label: "Flawless",       desc: "Perfect round - 10 in a row",      emoji: "👑", check: (_, c) => c.perfect },
  { id: "speed_3",     label: "Speed Demon",    desc: "3 answers in under 3 seconds",     emoji: "💨", check: (_, c) => c.speedWins >= 3 },
  { id: "animal_expert",label: "Animal Expert", desc: "25 animal questions correct",      emoji: "🦎", check: s => s.perCat.animals.correct >= 25 },
  { id: "space_nerd",   label: "Space Nerd",    desc: "25 space questions correct",       emoji: "🪐", check: s => s.perCat.space.correct >= 25 },
  { id: "human_encyc",  label: "Human Encyclopedia", desc: "25 body questions correct",   emoji: "🧠", check: s => s.perCat.body.correct >= 25 },
  { id: "weird_addict", label: "Weird Science Addict", desc: "25 weird questions correct", emoji: "⚗️", check: s => s.perCat.weird.correct >= 25 },
  { id: "smartasaurus", label: "Smartasaurus",  desc: "Score 91+ in a single round",      emoji: "🦖", check: (_, c) => c.score >= 91 },
  { id: "marathon",     label: "Marathon",      desc: "Play 10 rounds",                   emoji: "🏃", check: s => s.totalPlays >= 10 },
];

type Phase = "select" | "playing" | "results";

function PlayPage() {
  const data = Route.useLoaderData();
  const search = Route.useSearch();
  const allQuizzes = (data?.quizzes ?? []) as DBQuiz[];

  const buckets = useMemo(() => {
    const m: Record<Exclude<CatKey, "random">, DBQuiz[]> = { animals: [], space: [], body: [], weird: [] };
    for (const q of allQuizzes) m[classify(q)].push(q);
    return m;
  }, [allQuizzes]);

  const [stats, setStats] = useState<Stats>(DEFAULT_STATS);
  const [signedIn, setSignedIn] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const remoteSave = useServerFn(saveGameState);
  const remoteLoad = useServerFn(getGameState);

  useEffect(() => {
    setStats(loadStats());
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      const isIn = !!data.session;
      if (!active) return;
      setSignedIn(isIn);
      setCurrentUserId(data.session?.user?.id ?? null);
      if (!isIn) return;
      remoteLoad().then((res) => {
        if (!active || !res?.state) return;
        const remote = res.state as Partial<Stats>;
        const merged: Stats = {
          ...DEFAULT_STATS, ...remote,
          perCat: { ...DEFAULT_STATS.perCat, ...(remote.perCat ?? {}) },
        };
        setStats(merged);
        saveStats(merged);
      }).catch(() => {});
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSignedIn(!!s);
      setCurrentUserId(s?.user?.id ?? null);
    });
    return () => { active = false; sub.subscription.unsubscribe(); };
  }, [remoteLoad]);

  useEffect(() => {
    if (!signedIn) return;
    const t = setTimeout(() => { remoteSave({ data: { state: stats as any } }).catch(() => {}); }, 400);
    return () => clearTimeout(t);
  }, [stats, signedIn, remoteSave]);

  const [phase, setPhase] = useState<Phase>("select");
  const [cat, setCat] = useState<CatMeta>(CATEGORIES[0]);
  const [diff, setDiff] = useState<Difficulty>("beginner");
  const [round, setRound] = useState<GameQ[]>([]);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const [locked, setLocked] = useState(false);
  const [score, setScore] = useState(0);
  const { awardQuiz } = useUserXp();
  const [timeLeft, setTimeLeft] = useState(TIMER_BY_DIFF.beginner);
  const [streak, setStreak] = useState(0);
  const [bestStreakRound, setBestStreakRound] = useState(0);
  const [speedWins, setSpeedWins] = useState(0);
  const [lastDelta, setLastDelta] = useState(0);
  const [revealAt, setRevealAt] = useState(0);
  const [earned, setEarned] = useState<Achievement[]>([]);
  const [resumeCandidate, setResumeCandidate] = useState<InProgress | null>(null);
  const [lastRoundIds, setLastRoundIds] = useState<string[]>([]);
  const correctRef = useRef(0);
  const answersRef = useRef<HostAnswer[]>([]);
  const timerMax = TIMER_BY_DIFF[diff];

  // Check for a resumable in-progress round on mount.
  useEffect(() => {
    const p = loadInProgress();
    if (p && Array.isArray(p.round) && p.idx < p.round.length) setResumeCandidate(p);
  }, []);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const roundStartRef = useRef<number>(0);
  const openedQuizRef = useRef<string | null>(null);
  const logSessionFn = useServerFn(logQuizSession);

  function clearTimers() {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (autoRef.current) clearTimeout(autoRef.current);
    intervalRef.current = null; autoRef.current = null;
  }
  useEffect(() => () => clearTimers(), []);

  function pickByDifficulty(pool: DBQuiz[], want: Difficulty, avoid: Set<string> = new Set()): DBQuiz[] {
    if (pool.length === 0) return [];
    // Deduplicate the pool up front (safety net - never repeat within one round).
    const uniquePool = Array.from(new Map(pool.map((q) => [q.id, q])).values());
    // Sort by heuristic; slice into 3 tiers.
    const sorted = uniquePool.slice().sort((a, b) => difficultyScore(a) - difficultyScore(b));
    const n = sorted.length;
    const easyEnd = Math.max(1, Math.ceil(n * 0.5));
    const medEnd  = Math.max(easyEnd + 1, Math.ceil(n * 0.8));
    const easy = sorted.slice(0, easyEnd);
    const med  = sorted.slice(easyEnd, medEnd);
    const hard = sorted.slice(medEnd);
    const primary = want === "beginner" ? easy : want === "intermediate" ? med : hard;
    const topup = want === "expert" ? med.concat(easy)
                : want === "intermediate" ? easy.concat(hard)
                : med.concat(hard);
    // Prefer questions not recently played; only fall back to recent ones if we don't have enough.
    const primaryFresh = shuffle(primary.filter(q => !avoid.has(q.slug)));
    const primaryUsed  = shuffle(primary.filter(q => avoid.has(q.slug)));
    const topupFresh   = shuffle(topup.filter(q => !avoid.has(q.slug)));
    const topupUsed    = shuffle(topup.filter(q => avoid.has(q.slug)));
    const combined = primaryFresh.concat(topupFresh, primaryUsed, topupUsed);
    // Final dedupe by id so a question can never appear twice in a single round.
    const seen = new Set<string>();
    const out: DBQuiz[] = [];
    for (const q of combined) {
      if (seen.has(q.id)) continue;
      seen.add(q.id);
      out.push(q);
      if (out.length >= QUESTIONS_PER_GAME) break;
    }
    return out;
  }

  function startGame(meta: CatMeta, chosen: Difficulty = diff, resume?: InProgress) {
    if (resume) {
      setCat(resume.cat); setDiff(resume.diff); setRound(resume.round);
      setIdx(resume.idx); setScore(resume.score); setStreak(resume.streak);
      setBestStreakRound(resume.bestStreakRound); setSpeedWins(resume.speedWins); setLastDelta(0);
      setPicked(null); setLocked(false); setTimeLeft(TIMER_BY_DIFF[resume.diff]); setEarned([]);
      correctRef.current = resume.correctCount ?? 0;
      answersRef.current = resume.answers ?? [];
      roundStartRef.current = Date.now();
      setPhase("playing");
      track("game_resume", { category: resume.cat.key, difficulty: resume.diff, idx: resume.idx });
      return;
    }
    const pool = meta.key === "random" ? allQuizzes : buckets[meta.key];
    if (pool.length === 0) { toast.error("This category has no quizzes yet - try Mixed."); return; }
    const avoid = new Set(stats.recentSlugs ?? []);
    const chosenPool = pickByDifficulty(pool, chosen, avoid);
    const picks = chosenPool.map(prepQuestion);
    // Update recent-slugs memory so successive rounds don't repeat.
    const nextRecent = [...picks.map(p => p.q.slug), ...(stats.recentSlugs ?? [])].slice(0, RECENT_SLUG_LIMIT);
    const nextStats = { ...stats, recentSlugs: nextRecent };
    setStats(nextStats); saveStats(nextStats);
    setCat(meta); setDiff(chosen); setRound(picks); setIdx(0); setScore(0);
    setStreak(0); setBestStreakRound(0); setSpeedWins(0); setLastDelta(0);
    setPicked(null); setLocked(false); setTimeLeft(TIMER_BY_DIFF[chosen]); setEarned([]);
    correctRef.current = 0;
    answersRef.current = [];
    roundStartRef.current = Date.now();
    saveInProgress(null);
    setPhase("playing");
    track("game_start", { category: meta.key, difficulty: chosen, questions: picks.length });
    trackConversion("quiz", `/play/${meta.key}`);
  }

  useEffect(() => {
    const slug = search.quiz;
    if (!slug || openedQuizRef.current === slug || phase !== "select") return;
    const target = allQuizzes.find((q) => q.slug === slug);
    if (!target) return;
    openedQuizRef.current = slug;
    const targetCategory = classify(target);
    const meta = CATEGORIES.find((c) => c.key === targetCategory) ?? CATEGORIES[4];
    const rest = pickByDifficulty(buckets[targetCategory], diff, new Set([slug])).filter((q) => q.id !== target.id);
    const picks = [target, ...rest].slice(0, QUESTIONS_PER_GAME).map(prepQuestion);
    setCat(meta); setRound(picks); setIdx(0); setScore(0); setStreak(0);
    setBestStreakRound(0); setSpeedWins(0); setLastDelta(0); setPicked(null);
    setLocked(false); setTimeLeft(TIMER_BY_DIFF[diff]); setEarned([]);
    correctRef.current = 0; answersRef.current = []; roundStartRef.current = Date.now();
    saveInProgress(null); setPhase("playing");
    track("game_start", { category: meta.key, difficulty: diff, questions: picks.length, requested_quiz: slug });
  }, [search.quiz, allQuizzes, buckets, diff, phase]);

  function restartRound() {
    if (!round.length) return;
    track("game_restart", { category: cat.key, difficulty: diff });
    saveInProgress(null);
    startGame(cat, diff);
  }

  function nextOrFinish() {
    clearTimers();
    if (idx + 1 >= round.length) finishGame();
    else {
      setIdx(i => i + 1); setPicked(null); setLocked(false); setTimeLeft(timerMax);
    }
  }


  function lockAnswer(choice: number | null) {
    if (locked) return;
    clearTimers();
    setLocked(true); setPicked(choice); setRevealAt(Date.now());
    const current = round[idx];
    const isCorrect = choice !== null && choice === current.correctIdx;
    const elapsed = timerMax - timeLeft;
    // Record the original option index so the server can grade a challenge.
    answersRef.current[idx] = {
      o: choice === null ? null : current.q.options.indexOf(current.opts[choice]),
      s: Math.max(0, Math.min(3, Math.round((timeLeft / timerMax) * 3))),
    };

    // Anonymous accuracy tracking - fires for every player (not just signed-in).
    // Command Center uses these to compute quiz accuracy honestly.
    try {
      trackConversion(
        choice === null ? "quiz_timeout" : isCorrect ? "quiz_correct" : "quiz_wrong",
        `/play/${cat.key}/${current.q.slug}`,
      );
    } catch { /* never break the game */ }

    if (isCorrect) {
      const fast = elapsed <= 3;
      if (fast) setSpeedWins(v => v + 1);
      const newStreak = streak + 1;
      setStreak(newStreak);
      setBestStreakRound(b => Math.max(b, newStreak));
      // Score: base 5 + speed up to 3 + combo bonus (streak capped at 5)
      const speedBonus = Math.max(0, Math.min(3, Math.round((timeLeft / timerMax) * 3)));
      const comboMult = Math.min(5, newStreak);
      const delta = (5 + speedBonus) + (comboMult - 1) * 2;
      setLastDelta(delta);
      setScore(s => s + delta);
      // Shared site XP: once per question, so replays cannot farm points.
      awardQuiz(`play:${current.q.slug}`, delta);
      if (newStreak >= 3 && newStreak % 2 === 1) {
        toast.success(`${newStreak}× combo 🔥`, { duration: 1200 });
      }
    } else {
      setStreak(0); setLastDelta(0);
    }
    // User advances manually after reading the answer - no auto-advance timer.
  }


  function finishGame() {
    clearTimers();
    // Compute new stats + achievements
    const perfect = bestStreakRound >= round.length;
    // The authoritative correct-answer count is tracked as answers are locked in.
    const correctCount = correctRef.current;
    const ctx: RoundCtx = {
      cat: cat.key, correct: correctCount, score,
      bestStreak: bestStreakRound, speedWins, perfect,
    };
    const next: Stats = {
      ...stats,
      totalPlays: stats.totalPlays + 1,
      totalCorrect: stats.totalCorrect + correctCount,
      bestScore: Math.max(stats.bestScore, score),
      bestStreak: Math.max(stats.bestStreak, bestStreakRound),
      factsUnlocked: stats.factsUnlocked + correctCount,
      perCat: { ...stats.perCat },
    };
    if (cat.key !== "random") {
      const pc = next.perCat[cat.key];
      next.perCat[cat.key] = {
        correct: pc.correct + correctCount,
        bestScore: Math.max(pc.bestScore, score),
        plays: pc.plays + 1,
      };
    }
    const newly: Achievement[] = [];
    for (const a of ACHIEVEMENTS) {
      if (!next.achievements.includes(a.id) && a.check(next, ctx)) {
        next.achievements = [...next.achievements, a.id];
        newly.push(a);
      }
    }
    const historyEntry: RoundHistory = {
      cat: cat.key, difficulty: diff, score, correct: correctCount, total: round.length,
      bestStreak: bestStreakRound, playedAt: new Date().toISOString(),
    };
    next.history = [historyEntry, ...(next.history ?? [])].slice(0, 20);
    setStats(next); saveStats(next); setEarned(newly);
    setLastRoundIds(round.map(r => r.q.id));
    saveInProgress(null);
    // Persist session to admin analytics (anon-friendly, fire-and-forget).
    try {
      const duration_ms = roundStartRef.current ? Date.now() - roundStartRef.current : 0;
      trackConversion("quiz_complete", `/play/${cat.key}`);
      logSessionFn({
        data: {
          session_key: playerKey(),
          category: cat.key,
          difficulty: diff,
          score,
          correct: correctCount,
          total: round.length,
          best_streak: bestStreakRound,
          duration_ms,
          quiz_ids: round.map(r => r.q.id),
          source: "solo",
          user_id: currentUserId,
        },
      }).catch(() => {});
    } catch { /* never break the game */ }
    setPhase("results");
    track("game_complete", {
      category: cat.key, score, correct: correctCount,
      best_streak: bestStreakRound, perfect, achievements_unlocked: newly.length,
      new_pb: score > stats.bestScore,
    });
  }

  // Persist in-progress round so a refresh / accidental close can resume.
  useEffect(() => {
    if (phase !== "playing" || round.length === 0) return;
    saveInProgress({
      cat, diff, round, idx, score, streak, bestStreakRound, speedWins,
      correctCount: correctRef.current, answers: answersRef.current, savedAt: Date.now(),
    });
  }, [phase, cat, diff, round, idx, score, streak, bestStreakRound, speedWins, locked]);

  // Track correct count via ref to avoid re-render churn (declared earlier)
  useEffect(() => { correctRef.current = 0; }, [round]);
  useEffect(() => {
    if (!locked) return;
    const cur = round[idx];
    if (picked !== null && picked === cur.correctIdx) correctRef.current += 1;
  }, [locked, picked, idx, round]);

  // Per-question timer
  useEffect(() => {
    if (phase !== "playing" || locked) return;
    setTimeLeft(timerMax);
    intervalRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearTimers();
          setTimeout(() => lockAnswer(null), 0);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, phase]);

  if (phase === "select") return (
    <CategorySelect
      onPick={(c, d) => startGame(c, d)} buckets={buckets} totalQuizzes={allQuizzes.length}
      stats={stats} diff={diff} setDiff={setDiff}
      resume={resumeCandidate}
      onResume={() => { if (resumeCandidate) { setResumeCandidate(null); startGame(resumeCandidate.cat, resumeCandidate.diff, resumeCandidate); } }}
      onDiscardResume={() => { saveInProgress(null); setResumeCandidate(null); }}
    />
  );
  if (phase === "results") {
    return (
      <Results
        cat={cat} diff={diff} score={score} correct={correctRef.current} total={round.length}
        bestStreak={bestStreakRound} stats={stats} earned={earned}
        quizIds={lastRoundIds}
        answers={answersRef.current}
        onReplay={() => { track("game_replay", { category: cat.key }); startGame(cat); }}
        onChange={() => { track("game_change_category"); setPhase("select"); }}
        onNextCat={() => {
          const others = CATEGORIES.filter(c => c.key !== cat.key && c.key !== "random");
          const next = others[Math.floor(Math.random() * others.length)];
          track("game_next_category", { from: cat.key, to: next.key });
          startGame(next);
        }}
      />
    );
  }
  const current = round[idx];
  const canAdvance = locked;
  return (
    <Game
      cat={cat} idx={idx} total={round.length} score={score} timeLeft={timeLeft} timerMax={timerMax}
      question={current} picked={picked} locked={locked} streak={streak} lastDelta={lastDelta}
      onPick={lockAnswer} onExit={() => { clearTimers(); saveInProgress(null); setPhase("select"); }}
      onNext={nextOrFinish} canAdvance={canAdvance}
      onRestart={restartRound}
    />
  );
}

function scaleMax(n: number) { return n * 10; }

// ---- Category Select ----
const DIFF_META: Record<Difficulty, { label: string; tagline: string; timer: string; emoji: string }> = {
  beginner:     { label: "Beginner",     tagline: "Warm up. Feel smart.", timer: "25s", emoji: "🌱" },
  intermediate: { label: "Intermediate", tagline: "You've got this.",     timer: "18s", emoji: "🔥" },
  expert:       { label: "Expert",       tagline: "Bring the pain.",       timer: "12s", emoji: "🦖" },
};

function CategorySelect({
  onPick, buckets, totalQuizzes, stats, diff, setDiff, resume, onResume, onDiscardResume,
}: {
  onPick: (c: CatMeta, d: Difficulty) => void;
  buckets: Record<Exclude<CatKey, "random">, DBQuiz[]>;
  totalQuizzes: number; stats: Stats;
  diff: Difficulty; setDiff: (d: Difficulty) => void;
  resume?: InProgress | null;
  onResume?: () => void;
  onDiscardResume?: () => void;
}) {
  const totalAch = ACHIEVEMENTS.length;
  const unlockedAch = stats.achievements.length;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-5 sm:py-8">
        <div className="mb-5 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Home
          </Link>
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-primary">
            <Sparkles className="h-3 w-3" /> 10 cards · {DIFF_META[diff].timer} each
          </span>
        </div>

        <h1 className="font-display text-[2.6rem] uppercase leading-[0.95] tracking-tight sm:text-6xl">
          Tap. Reveal.<br/>Get smarter.
        </h1>
        <p className="mt-3 max-w-xl text-sm text-muted-foreground sm:text-base">
          A science curiosity game. One round = ten cards = ten "holy shit" facts.
        </p>

        {resume && (
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-primary/40 bg-primary/10 p-4">
            <div className="text-sm">
              <div className="font-display text-base uppercase tracking-wide">Resume your round?</div>
              <div className="text-xs text-muted-foreground">
                {resume.cat.label} · {DIFF_META[resume.diff].label} · question {resume.idx + 1}/{resume.round.length}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={onResume} className="rounded-full bg-primary px-4 py-2 text-xs font-bold uppercase tracking-widest text-primary-foreground hover:brightness-110">Resume</button>
              <button onClick={onDiscardResume} className="rounded-full border border-border px-4 py-2 text-xs font-bold uppercase tracking-widest hover:border-primary">Discard</button>
            </div>
          </div>
        )}

        {/* Difficulty picker */}
        <div className="mt-5">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Difficulty</div>
          <div className="grid grid-cols-3 gap-2">
            {(Object.keys(DIFF_META) as Difficulty[]).map((d) => {
              const meta = DIFF_META[d];
              const active = diff === d;
              return (
                <button
                  key={d}
                  onClick={() => setDiff(d)}
                  className={`rounded-2xl border p-3 text-left transition active:scale-[0.98] ${active ? "border-primary bg-primary/10 ring-2 ring-primary/40" : "border-border bg-card/40 hover:border-primary/40"}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xl">{meta.emoji}</span>
                    <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{meta.timer}</span>
                  </div>
                  <div className="mt-1 font-display text-sm uppercase">{meta.label}</div>
                  <div className="text-[10px] text-muted-foreground">{meta.tagline}</div>
                </button>
              );
            })}
          </div>
        </div>


        {/* Personal stats strip */}
        {stats.totalPlays > 0 && (
          <div className="mt-5 grid grid-cols-4 gap-2 rounded-2xl border border-border bg-card/50 p-3">
            <MiniStat label="Best" value={stats.bestScore} icon={<Trophy className="h-3 w-3 text-amber-400" />} />
            <MiniStat label="Streak" value={stats.bestStreak} icon={<Flame className="h-3 w-3 text-orange-400" />} />
            <MiniStat label="Facts" value={stats.factsUnlocked} icon={<Sparkles className="h-3 w-3 text-primary" />} />
            <MiniStat label="Badges" value={`${unlockedAch}/${totalAch}`} icon={<Award className="h-3 w-3 text-fuchsia-400" />} />
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {CATEGORIES.map((c) => {
            const count = c.key === "random" ? totalQuizzes : buckets[c.key]?.length ?? 0;
            const ready = count >= QUESTIONS_PER_GAME;
            const pc = c.key === "random" ? null : stats.perCat[c.key];
            const mastery = pc ? Math.min(100, Math.round((pc.correct / 25) * 100)) : 0;
            return (
              <button
                key={c.key}
                onClick={() => onPick(c, diff)}
                disabled={!ready && c.key !== "random"}
                className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br ${c.gradient} p-5 text-left transition active:scale-[0.98] hover:scale-[1.02] hover:border-white/30 disabled:opacity-40`}
              >
                <div className="flex items-start justify-between">
                  <span className="text-5xl drop-shadow-lg">{c.emoji}</span>
                  {pc && pc.bestScore > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-black/40 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-300">
                      <Trophy className="h-3 w-3" /> PB {pc.bestScore}
                    </span>
                  )}
                </div>
                <div className="mt-5">
                  <div className="font-display text-2xl uppercase tracking-wide text-white">{c.label}</div>
                  <div className="text-sm text-white/70">{c.tagline}</div>
                </div>
                {pc && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-white/70">
                      <span>{c.masterTitle}</span><span>{mastery}%</span>
                    </div>
                    <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-black/30">
                      <div className="h-full bg-white/80" style={{ width: `${mastery}%` }} />
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Achievements preview */}
        <div className="mt-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-lg uppercase tracking-wide">Badges</h2>
            <span className="text-xs text-muted-foreground">{unlockedAch}/{totalAch} unlocked</span>
          </div>
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
            {ACHIEVEMENTS.map(a => {
              const got = stats.achievements.includes(a.id);
              return (
                <div key={a.id} title={`${a.label} - ${a.desc}`} className={`relative aspect-square rounded-xl border p-2 text-center ${got ? "border-amber-400/40 bg-amber-500/10" : "border-border bg-muted/30"}`}>
                  <div className={`text-2xl ${got ? "" : "grayscale opacity-40"}`}>{a.emoji}</div>
                  <div className={`mt-0.5 text-[9px] font-bold uppercase leading-tight ${got ? "text-foreground" : "text-muted-foreground"}`}>{a.label}</div>
                  {!got && <Lock className="absolute right-1 top-1 h-2.5 w-2.5 text-muted-foreground" />}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, icon }: { label: string; value: number | string; icon: React.ReactNode }) {
  return (
    <div className="text-center">
      <div className="flex items-center justify-center gap-1 font-display text-lg leading-none">{icon}{value}</div>
      <div className="mt-1 text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{label}</div>
    </div>
  );
}

// ---- Game screen ----
function Game({
  cat, idx, total, score, timeLeft, timerMax, question, picked, locked, streak, lastDelta,
  onPick, onExit, onNext, canAdvance, onRestart,
}: {
  cat: CatMeta; idx: number; total: number; score: number; timeLeft: number; timerMax: number;
  question: GameQ; picked: number | null; locked: boolean; streak: number; lastDelta: number;
  onPick: (i: number) => void; onExit: () => void; onNext: () => void; canAdvance: boolean;
  onRestart?: () => void;
}) {

  const progress = ((idx + (locked ? 1 : 0)) / total) * 100;
  const correctIdx = question.correctIdx;
  const isCorrect = picked !== null && picked === correctIdx;
  const isWrong = locked && picked !== null && picked !== correctIdx;
  const isTimeout = locked && picked === null;

  return (
    <div className={`relative min-h-screen overflow-hidden bg-gradient-to-br ${cat.gradient} text-white`}>
      <div aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.08]">
        <span className="select-none text-[22rem] leading-none sm:text-[28rem]">{cat.emoji}</span>
      </div>
      <div className="absolute inset-0 bg-black/35" />

      <div className="relative mx-auto flex min-h-screen max-w-2xl flex-col px-4 py-4 sm:px-5">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <button onClick={onExit} className="inline-flex items-center gap-1 rounded-full bg-black/40 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-white/80 backdrop-blur active:scale-95">
            <ArrowLeft className="h-3 w-3" /> Quit
          </button>
          <div className="font-mono text-xs font-bold uppercase tracking-widest text-white/80">{idx + 1}/{total}</div>
          <div className="flex items-center gap-1.5">
            {streak >= 2 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-orange-500/30 px-2.5 py-1 text-[10px] font-bold text-orange-100 ring-1 ring-orange-300/50 animate-fade-in">
                <Flame className="h-3 w-3" /> {streak}×
              </span>
            )}
            {onRestart && (
              <button
                onClick={() => { if (confirm("Restart this round with new questions?")) onRestart(); }}
                title="Restart round"
                className="inline-flex items-center justify-center rounded-full bg-black/40 p-2 text-white/80 backdrop-blur hover:text-white active:scale-95"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            )}
            <span className="inline-flex items-center gap-1 rounded-full bg-black/40 px-3 py-1.5 text-xs font-bold text-white backdrop-blur">
              <Trophy className="h-3 w-3 text-amber-300" /> {score}
            </span>
          </div>
        </div>

        {/* Progress + timer */}
        <div className="mt-3">
          <div className="mb-1 flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-white/80">
            <span>Question {idx + 1} of {total}</span>
            <span className="tabular-nums">{Math.round(progress)}%</span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-white/15 ring-1 ring-white/10">
            <div className="h-full bg-gradient-to-r from-amber-300 to-white transition-[width] duration-300" style={{ width: `${progress}%` }} />
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Timer className={`h-3.5 w-3.5 ${timeLeft <= 4 ? "text-red-300 animate-pulse" : "text-white/70"}`} />
            <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/10">
              <div
                className={`h-full transition-[width] duration-1000 ease-linear ${timeLeft <= 4 ? "bg-red-400" : "bg-amber-300"}`}
                style={{ width: `${(timeLeft / timerMax) * 100}%` }}
              />
            </div>
            <span className={`w-6 text-right font-mono text-xs tabular-nums ${timeLeft <= 4 ? "text-red-300 font-bold" : "text-white/80"}`}>{timeLeft}</span>
          </div>
        </div>

        {/* Card */}
        <div className="mt-5 flex flex-1 flex-col">
          <div key={idx} className={`animate-scale-in rounded-3xl border border-white/15 bg-black/40 p-5 shadow-2xl backdrop-blur-md ${isCorrect ? "ring-2 ring-emerald-400" : isWrong ? "ring-2 ring-red-400" : ""}`}>
            <p className="text-[10px] font-bold uppercase tracking-widest text-amber-300/90">{cat.label} · Card {idx + 1}</p>
            <h2 className="mt-2 font-display text-2xl uppercase leading-[1.05] tracking-tight sm:text-3xl">
              {question.q.question}
            </h2>
          </div>

          {/* Options */}
          <div className="mt-4 grid grid-cols-1 gap-2.5">
            {question.opts.map((opt, i) => {
              const isPicked = picked === i;
              const isThisCorrect = i === correctIdx;
              let cls = "border-white/20 bg-white/10 hover:bg-white/20 active:scale-[0.98]";
              if (locked) {
                if (isThisCorrect) cls = "border-emerald-300 bg-emerald-500/40 ring-2 ring-emerald-300 animate-scale-in";
                else if (isPicked) cls = "border-red-300 bg-red-500/30";
                else cls = "border-white/10 bg-white/5 opacity-50";
              } else if (isPicked) {
                cls = "border-white bg-white/25";
              }
              return (
                <button
                  key={i}
                  disabled={locked}
                  onClick={() => onPick(i)}
                  className={`flex min-h-[56px] items-center justify-between rounded-2xl border px-4 py-3 text-left text-[15px] font-medium backdrop-blur transition ${cls}`}
                >
                  <span className="flex items-center gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-black/40 text-xs font-bold text-white/80">
                      {String.fromCharCode(65 + i)}
                    </span>
                    <span className="leading-snug">{opt}</span>
                  </span>
                  {locked && isThisCorrect && <Check className="h-5 w-5 shrink-0 text-emerald-100" />}
                  {locked && !isThisCorrect && isPicked && <X className="h-5 w-5 shrink-0 text-red-200" />}
                </button>
              );
            })}
          </div>

          {/* Reveal fact card */}
          {locked && (
            <div className="mt-4 animate-fade-in rounded-2xl border border-amber-300/30 bg-gradient-to-br from-black/70 to-black/40 p-4 shadow-xl backdrop-blur">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-amber-300">
                  <Zap className="h-3 w-3" />
                  {isCorrect ? `+${lastDelta} · Nailed it` : isTimeout ? "Time's up" : "Holy shit fact"}
                </span>
                {isCorrect && streak >= 3 && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-orange-300">
                    <Flame className="h-3 w-3" /> {streak}× combo
                  </span>
                )}
              </div>
              <p className="text-[15px] leading-relaxed text-white/95">{question.q.explanation}</p>
            </div>
          )}
        </div>

        {/* Sticky next button */}
        {locked && (
          <div className="sticky bottom-0 -mx-4 mt-3 bg-gradient-to-t from-black/80 to-transparent px-4 pb-4 pt-3 sm:-mx-5 sm:px-5">
            <button
              onClick={onNext}
              disabled={!canAdvance}
              className={`flex w-full items-center justify-center gap-2 rounded-full px-5 py-4 text-sm font-bold uppercase tracking-wide transition active:scale-[0.98] ${canAdvance ? "bg-white text-black" : "bg-white/40 text-black/60"}`}
            >
              {idx + 1 >= total ? "See your rank" : "Next card"} <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Results ----
function Results({
  cat, diff, score, correct, total, bestStreak, stats, earned, quizIds, answers,
  onReplay, onChange, onNextCat,
}: {
  cat: CatMeta; diff: Difficulty; score: number; correct: number; total: number; bestStreak: number;
  stats: Stats; earned: Achievement[]; quizIds: string[]; answers: HostAnswer[];
  onReplay: () => void; onChange: () => void; onNextCat: () => void;
}) {
  const max = scaleMax(total) + (total - 1) * 8; // roughly
  const pct = Math.min(100, Math.round((score / 100) * 100)); // score is roughly 0-100+, clamp
  const rank = rankFor(Math.min(100, score));
  const isPB = score >= stats.bestScore && score > 0;

  const createChallengeFn = useServerFn(createChallenge);
  const [challengeUrl, setChallengeUrl] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  function shareResult() {
    const text = `I scored ${score} on Smartasaurus${bestStreak >= 3 ? ` with a ${bestStreak}× streak` : ""} - Rank: ${rank.label} ${rank.emoji}. Beat me 👇\nhttps://smartasaurus.com/play`;
    track("game_share", { rank: rank.label, score, best_streak: bestStreak });
    if (typeof navigator !== "undefined" && (navigator as any).share) {
      (navigator as any).share({ title: "Smartasaurus", text, url: "https://smartasaurus.com/play" }).catch(() => {});
    } else if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => toast.success("Copied to clipboard")).catch(() => {});
    }
  }

  async function makeChallenge() {
    if (creating) return;
    if (!quizIds.length) { toast.error("No questions to challenge with"); return; }
    setCreating(true);
    try {
      const hostName = (() => { try { return localStorage.getItem("smartasaurus.player.name") || "A friend"; } catch { return "A friend"; } })();
      const res: any = await createChallengeFn({
        data: {
          category: cat.key, difficulty: diff, quiz_ids: quizIds,
          host_name: hostName,
          answers: answers.length === quizIds.length ? answers.map(a => ({ option: a.o, speed: a.s })) : undefined,
        },
      });
      if (!res?.ok) { toast.error(res?.error ?? "Could not create challenge"); return; }
      const url = `${window.location.origin}/challenge/${res.challenge.code}`;
      setChallengeUrl(url);
      track("challenge_created", { category: cat.key, difficulty: diff, score });
      const hostScore = res.challenge.host_score ?? score;
      const text = `Beat my ${hostScore} on Smartasaurus 🧠 - same 10 questions:\n${url}`;
      if (typeof navigator !== "undefined" && (navigator as any).share) {
        (navigator as any).share({ title: "Smartasaurus challenge", text, url }).catch(() => {});
      } else if (typeof navigator !== "undefined" && navigator.clipboard) {
        navigator.clipboard.writeText(url).then(() => toast.success("Challenge link copied")).catch(() => {});
      }
    } catch { toast.error("Could not create challenge"); }
    finally { setCreating(false); }
  }

  function copyChallenge() {
    if (!challengeUrl) return;
    navigator.clipboard.writeText(challengeUrl).then(() => toast.success("Copied")).catch(() => {});
  }

  return (
    <div className={`relative min-h-screen overflow-hidden bg-gradient-to-br ${cat.gradient} text-white`}>
      <div aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.08]">
        <span className="select-none text-[24rem] leading-none">{rank.emoji}</span>
      </div>
      <div className="absolute inset-0 bg-black/40" />

      <div className="relative mx-auto flex min-h-screen max-w-2xl flex-col px-4 py-6 sm:px-5 sm:py-8">
        <div className="text-center animate-fade-in">
          {isPB && (
            <div className="mx-auto mb-3 inline-flex items-center gap-1 rounded-full bg-amber-400/20 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-200 ring-1 ring-amber-300/50">
              <Crown className="h-3 w-3" /> New Personal Best
            </div>
          )}
          <p className="text-[10px] font-bold uppercase tracking-widest text-white/70">Round complete</p>
          <div className="mt-4 text-7xl animate-scale-in">{rank.emoji}</div>
          <div className="mt-2 font-display text-4xl uppercase leading-none tracking-tight sm:text-6xl">{rank.label}</div>
          <p className="mt-3 text-base text-white/80 sm:text-lg">{rank.line}</p>

          <div className="mt-6 grid grid-cols-4 gap-2">
            <Stat label="Score" value={`${score}`} />
            <Stat label="Correct" value={`${correct}/${total}`} />
            <Stat label="Streak" value={`${bestStreak}×`} />
            <Stat label="PB" value={`${stats.bestScore}`} />
          </div>
        </div>

        {/* Achievements unlocked */}
        {earned.length > 0 && (
          <div className="mt-6 rounded-2xl border border-amber-300/40 bg-amber-500/10 p-4 backdrop-blur animate-fade-in">
            <div className="mb-2 flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-amber-200">
              <Star className="h-3 w-3" /> {earned.length} badge{earned.length > 1 ? "s" : ""} unlocked
            </div>
            <div className="flex flex-wrap gap-2">
              {earned.map(a => (
                <div key={a.id} className="flex items-center gap-2 rounded-xl bg-black/40 px-3 py-2">
                  <span className="text-2xl">{a.emoji}</span>
                  <div>
                    <div className="text-xs font-bold uppercase">{a.label}</div>
                    <div className="text-[10px] text-white/70">{a.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA stack */}
        <div className="mt-6 grid grid-cols-1 gap-2.5">
          <button onClick={onReplay} className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-4 text-sm font-bold uppercase tracking-wide text-black active:scale-[0.98]">
            <RotateCcw className="h-4 w-4" /> One more round
          </button>
          <button onClick={onNextCat} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 bg-white/10 px-5 py-3.5 text-sm font-bold uppercase tracking-wide text-white backdrop-blur active:scale-[0.98]">
            <Shuffle className="h-4 w-4" /> Random category
          </button>
          <div className="grid grid-cols-2 gap-2.5">
            <button onClick={shareResult} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-black/30 px-4 py-3 text-xs font-bold uppercase tracking-wide text-white/90 backdrop-blur active:scale-[0.98]">
              <Share2 className="h-4 w-4" /> Share rank
            </button>
            <button onClick={onChange} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-black/30 px-4 py-3 text-xs font-bold uppercase tracking-wide text-white/90 backdrop-blur active:scale-[0.98]">
              Change category
            </button>
          </div>
        </div>

        {/* Challenge a friend (async multiplayer) */}
        <div className="mt-4 rounded-2xl border border-primary/40 bg-primary/10 p-4 backdrop-blur">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/25 text-primary"><Users className="h-4 w-4" /></div>
            <div className="flex-1">
              <div className="font-display text-base uppercase tracking-wide text-white">Challenge a friend</div>
              <div className="text-xs text-white/70">Same 10 questions. Same category & difficulty. Who wins?</div>
              {challengeUrl ? (
                <div className="mt-3 flex items-center gap-2 rounded-xl bg-black/40 p-2">
                  <input readOnly value={challengeUrl} className="flex-1 bg-transparent text-xs text-white/90 outline-none" />
                  <button onClick={copyChallenge} className="inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-white/20">
                    <Copy className="h-3 w-3" /> Copy
                  </button>
                </div>
              ) : (
                <button
                  onClick={makeChallenge}
                  disabled={creating}
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-xs font-bold uppercase tracking-widest text-primary-foreground hover:brightness-110 disabled:opacity-60"
                >
                  <Share2 className="h-4 w-4" /> {creating ? "Creating…" : "Create challenge link"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Ecosystem */}
        <div className="mt-8 rounded-2xl border border-white/15 bg-black/40 p-5 backdrop-blur">
          <div className="mb-3 text-[10px] font-bold uppercase tracking-widest text-amber-300">Keep the dopamine going</div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <Link to="/me" onClick={() => track("game_outbound", { to: "profile" })} className="rounded-xl bg-white/10 px-3 py-3 font-semibold hover:bg-white/20">→ Back to my profile</Link>
            <Link to="/discoveries" onClick={() => track("game_outbound", { to: "discoveries" })} className="rounded-xl bg-white/10 px-3 py-3 font-semibold hover:bg-white/20">→ Wild discoveries</Link>
            <button type="button" onClick={onReplay} className="rounded-xl bg-white/10 px-3 py-3 text-left font-semibold hover:bg-white/20">→ More questions</button>
            <a href="https://www.instagram.com/channel/Abb0uo2Ci9GzCQmM/" target="_blank" rel="noopener noreferrer" onClick={() => track("game_outbound", { to: "broadcast" })} className="rounded-xl bg-white/10 px-3 py-3 font-semibold hover:bg-white/20">→ Broadcast Channel</a>
            <Link to="/shop" onClick={() => track("game_outbound", { to: "shop" })} className="rounded-xl bg-white/10 px-3 py-3 font-semibold hover:bg-white/20">→ Shop</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-white/15 bg-black/30 p-2.5 text-center backdrop-blur">
      <div className="font-display text-xl text-white tabular-nums">{value}</div>
      <div className="mt-0.5 text-[9px] font-bold uppercase tracking-widest text-white/60">{label}</div>
    </div>
  );
}
